import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  type CreateKanMutationError,
  type CreateKoMutationError,
  type CreateMokuMutationError,
  getListAccountingTypesQueryKey,
  getListMastersFlatQueryKey,
  useCreateKan,
  useCreateKo,
  useCreateMoku,
} from '@/entities/masters/api/generated/api';
import { toast } from '@/shared/ui';

export const useExpenditureItemsCreateMutation = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const invalidateAndNotify = () => {
    toast.success(
      t('common.messages.create-success', {
        name: t('master-management.menu.expenditure-items'),
      }),
    );
    queryClient.invalidateQueries({
      queryKey: getListMastersFlatQueryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: getListAccountingTypesQueryKey(),
    });
    onSuccess();
  };

  const handleError = (error: { response?: { data: { error_code?: string } } }) => {
    if (error.response?.data.error_code === 'VALIDATION_ERROR') {
      toast.error(t('master-management.expenditure-items.error.duplicate-code'));
      return;
    }
    toast.error(
      t('common.messages.create-failed', {
        name: t('master-management.menu.expenditure-items'),
      }),
    );
  };

  const kanMutation = useCreateKan({
    request: { suppressToast: true },
    mutation: {
      onSuccess: invalidateAndNotify,
      onError: (error: CreateKanMutationError) => handleError(error),
    },
  });

  const koMutation = useCreateKo({
    request: { suppressToast: true },
    mutation: {
      onSuccess: invalidateAndNotify,
      onError: (error: CreateKoMutationError) => handleError(error),
    },
  });

  const mokuMutation = useCreateMoku({
    request: { suppressToast: true },
    mutation: {
      onSuccess: invalidateAndNotify,
      onError: (error: CreateMokuMutationError) => handleError(error),
    },
  });

  return {
    kanMutation,
    koMutation,
    mokuMutation,
    isPending: kanMutation.isPending || koMutation.isPending || mokuMutation.isPending,
  };
};
